import EventStepper from 'Components/EventStepper';
import { StepperHeaderFormInputPair, StepperHeaderValuePair } from 'Components/EventStepper/components';
import { fm } from 'Components/FormatMessage';
import FormRow from 'Components/FormRow';
import FormSection from 'Components/FormSection';
import InputHandler from 'Components/InputHandler';
import type { IFormContext } from 'Containers/FormContextHandler';
import { withFormContext } from 'Containers/FormContextHandler';
import { formatPartialDate } from 'Utility/date';
import * as React from 'react';
import { isEmpty } from 'Utility/ramdaReplacement';
import type { IHifuAdverseEffect } from 'neuro-schemas/hifu';

const HifuForm = ({ formData, editing }: IFormContext<IDocumentWithTypeV2<'hifu'>>): React.JSX.Element => {
  return (
    <>
      <FormSection>
        <FormRow title="thalamotomyHifu.thalamotomy.date">
          <InputHandler
            name="date"
            type="PartialDate"
            formData={formData}
            editing={!!editing}
            dateDefault="now"
            isNotCancellable={true}
          />
        </FormRow>
        <FormRow title="thalamotomyHifu.hifu.treatmentTarget">
          <InputHandler
            name="treatmentTarget"
            type="Radio"
            formData={formData}
            editing={!!editing}
            options={['talamusVIMRight', 'talamusVIMLeft', 'talamusVIMBoth', 'stnRight', 'stnLeft', 'stnBoth', 'other']}
            optionFormatter={(opt: string | number): React.JSX.Element => fm(`thalamotomyHifu.hifu.opts.${opt}`)}
          />
        </FormRow>
        <FormRow condition={formData.document.treatmentTarget === 'other'}>
          <InputHandler
            type="TextArea"
            name="treatmentTargetOther"
            formData={formData}
            editing={!!editing}
            placeholder="thalamotomyHifu.hifu.whatOther"
          />
        </FormRow>
      </FormSection>
      <FormSection>
        <EventStepper
          name="adverseEffects"
          formData={formData}
          stepLabelText={(d: IHifuAdverseEffect): string => formatPartialDate(d.date)}
          stepContent={(d: IHifuAdverseEffect): React.JSX.Element => (
            <React.Fragment>
              <StepperHeaderValuePair
                header={fm('thalamotomyHifu.adverseEffect')}
                value={
                  d.adverseEffects &&
                  !isEmpty(d.adverseEffects) &&
                  d.adverseEffects.map(ae => {
                    return <div key={ae}>{fm(`thalamotomyHifu.opts.${ae}`)}</div>;
                  })
                }
              />
              <StepperHeaderValuePair
                header={fm('thalamotomyHifu.adverseEffectAdditionalInformation')}
                value={d.additionalInformation}
              />
            </React.Fragment>
          )}
          addNewTextHeader="thalamotomyHifu.newAdverseEffect"
          addNewTextButton="thalamotomyHifu.newAdverseEffect"
          previousEventsTextHeader="thalamotomyHifu.previousAdverseEffects"
          noPreviousEventsTextHeader="thalamotomyHifu.noPreviousAdverseEffects"
          editingElements={(index: number, onChange: IFormData['onChange']): React.JSX.Element => (
            <React.Fragment>
              <StepperHeaderFormInputPair
                header={fm('general.date')}
                input={
                  <InputHandler
                    type="PartialDate"
                    editing={true}
                    name="date"
                    formData={{
                      onChange,
                      document: { date: formData.document.adverseEffects?.[index]?.date ?? '' },
                    }}
                    dateDefault="now"
                    isNotCancellable={true}
                    dateHook={{
                      dateHookFloor: formData.document.date,
                    }}
                  />
                }
              />
              <StepperHeaderFormInputPair
                header={fm('thalamotomyHifu.adverseEffect')}
                input={
                  <InputHandler
                    type="Checkbox"
                    name="adverseEffects"
                    editing={true}
                    formData={{
                      onChange,
                      document: { adverseEffects: formData.document.adverseEffects?.[index]?.adverseEffects },
                    }}
                    options={[
                      'dysarthria',
                      'stability',
                      'treatedArmAtaxia',
                      'gait',
                      'paresthesy',
                      'cramp',
                      'doubleImages',
                      'infection',
                      'sweating',
                      'cerebralHemorrhage',
                      'hallucination',
                      'suboptimalResponse',
                      'other',
                    ]}
                    optionFormatter={opt => fm(`thalamotomyHifu.opts.${opt}`)}
                  />
                }
              />
              <StepperHeaderFormInputPair
                header={fm('thalamotomyHifu.adverseEffectAdditionalInformation')}
                input={
                  <InputHandler
                    name="additionalInformation"
                    type="TextArea"
                    formData={{
                      onChange,
                      document: {
                        additionalInformation: formData.document.adverseEffects?.[index]?.additionalInformation,
                      },
                    }}
                    editing={true}
                    placeholder={'thalamotomyHifu.adverseEffectAdditionalInformation'}
                  />
                }
              />
            </React.Fragment>
          )}
          viewing={!editing}
        />
      </FormSection>
    </>
  );
};

export default withFormContext(HifuForm);
